// graft(#97c): copied from genome/v4 sdk/src/plugins/contract.ts — v5 owns this module, no live dep on v3/v4.
/**
 * ComposableGenomePlugin — THE single definition of the plugin contract.
 *
 * v3 defined this contract two-to-three times (tree-nav, GenomeBuilderV3
 * inline mirror, ProtoPlugin) bridged by unchecked double-casts in 14
 * packages. In v4 every consumer — the builder's `.use()`, `definePlugin()`,
 * the app shell's editor resolution — imports this file's types. Entity
 * shapes come from the genome module; nothing here redeclares them.
 *
 * @module @ubiclouds/v5-sdk/plugins/contract
 */

import type { ComponentType } from 'react';
import type { EntityDef } from '../genome/types.js';

export type TrustTier = 'core' | 'first-party' | 'third-party';

/**
 * Backend contract declaration for a composable genome plugin.
 */
export interface BackendContract<T = unknown> {
  readonly entityPath: string;
  readonly interfaceName: string;
  readonly providers: readonly string[];
  readonly _phantom?: T;
}

/**
 * A genome plugin that contributes entities to the entity-surface graph.
 *
 * Editor resolution contract: `editors` is keyed by **component entity id** —
 * the `{id}` segment of a `{pluginId}:component/{id}` entity. A route whose
 * `routes-to` ref targets `{pluginId}:component/{id}` resolves to
 * `editors[id]` of the plugin with that id. There is no candidate-key
 * probing; an unresolvable target fails genome validation (gate 4).
 */
export interface ComposableGenomePlugin {
  readonly id: string;
  readonly name: string;
  readonly version?: string;
  readonly description?: string;
  readonly capabilities?: readonly string[];
  readonly trustTier?: TrustTier;
  /** Component-entity-id → editor component. See contract note above. */
  readonly editors?: Readonly<Record<string, ComponentType>>;

  /**
   * Additional namespaces (beyond `id`) this plugin may contribute entities
   * under. First-class multi-namespace support — v3's single-namespace guard
   * forced hosts to fabricate synthetic plugins to smuggle cross-namespace
   * entities past it. Declare the edge instead.
   */
  readonly contributesTo?: readonly string[];

  /** Return the plugin's entity contributions. */
  entities(): EntityDef[];

  readonly backendContract?: BackendContract;
}

/**
 * Optional configuration passed to `.use()` for backend provider binding.
 */
export interface GenomeSurfaceConfig {
  readonly backend?: {
    readonly provider: string;
    readonly config?: Readonly<Record<string, unknown>>;
  };
}

/**
 * Thrown when a plugin contributes an entity whose namespace is neither the
 * plugin's id nor one of its declared `contributesTo` namespaces.
 */
export class NamespaceViolationError extends Error {
  constructor(
    public readonly pluginId: string,
    public readonly entityPath: string,
    allowedNamespaces: readonly string[],
  ) {
    super(
      `Entity path "${entityPath}" is not under any namespace declared by plugin "${pluginId}" ` +
      `(allowed: ${allowedNamespaces.map((n) => `"${n}:"`).join(', ')}). ` +
      `Plugins may only contribute entities under their own id or declared contributesTo namespaces.`,
    );
    this.name = 'NamespaceViolationError';
  }
}

/**
 * Thrown when a plugin-contributed entity derives an invalid entity path —
 * an empty id segment, an unknown kind, or a kind that contradicts the
 * entity's own `kind` field. v3 silently produced dangling paths like
 * `pluginId:store/` that collided per kind.
 */
export class InvalidPluginEntityError extends Error {
  constructor(
    public readonly pluginId: string,
    message: string,
  ) {
    super(`Plugin "${pluginId}": ${message}`);
    this.name = 'InvalidPluginEntityError';
  }
}
